import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Font} from '@samuelmeuli/font-manager';
import {templates} from 'features/beersManager/typescript';
import {IPost} from 'typescript/models/Post';
import handlePostThumbnailFitTypes from 'utils/helpers/handlePostThumbnailFitTypes';
import transformPostType from 'utils/helpers/transformPostType';

import {PostTypes} from './constants';
import {IField, postGeneratorSteps, postThumbnailFitTypes} from './typescript';

interface IState {
  step: postGeneratorSteps;
  postType: string;
  postWidth: number;
  postHeight: number;
  fields: IField[];
  singleField: IField | null;
  backgroundImage: string;
  font: Font | null;
  activeTemplate: templates;
  isPriceChecked: boolean;
  postThumbnailFitType: postThumbnailFitTypes;
}

const initialState: IState = {
  step: postGeneratorSteps.manageBeers,
  postType: 'instagramPortrait',
  postWidth: PostTypes.instagramPortrait.width,
  postHeight: PostTypes.instagramPortrait.height,
  fields: [],
  singleField: null,
  backgroundImage: '',
  font: null,
  isPriceChecked: false,
  activeTemplate: templates.Standard,
  postThumbnailFitType: postThumbnailFitTypes.cover,
};

export const PostGeneratorNamespace = 'postGenerator';

const postGeneratorSlice = createSlice({
  name: PostGeneratorNamespace,
  initialState,
  reducers: {
    setStepAC: (state, action: PayloadAction<postGeneratorSteps>) => {
      state.step = action.payload;
    },
    setPostTypeAC: (state, action: PayloadAction<string>) => {
      state.postType = action.payload;
      state.postWidth = PostTypes[action.payload].width;
      state.postHeight = PostTypes[action.payload].height;
    },
    setFieldsAC: (state, action: PayloadAction<IField[]>) => {
      state.fields = action.payload;
    },
    setSingleFieldAC: (state, action: PayloadAction<IField>) => {
      state.singleField = action.payload;
    },
    setBackgroundImageAC: (state, action: PayloadAction<string>) => {
      state.backgroundImage = action.payload;
    },
    setFontAC: (state, action: PayloadAction<Font>) => {
      state.font = action.payload;
    },
    setActiveTemplateAC: (state, action: PayloadAction<templates>) => {
      state.activeTemplate = action.payload;
    },
    setInitialState() {
      return initialState;
    },
    setIsPriceCheckedAC: (state, action: PayloadAction<IState['isPriceChecked']>) => {
      state.isPriceChecked = action.payload;
    },
    setPostThumbnailFitTypeAC: (state, action: PayloadAction<IState['postThumbnailFitType']>) => {
      state.postThumbnailFitType = action.payload;
    },
    setArchiveValuesAC: (state, action: PayloadAction<IPost>) => {
      const transformedPostType = transformPostType(action.payload.attributes.social_network_kind);
      const postThumbnailType = handlePostThumbnailFitTypes(action.payload.attributes.post_thumbnail_fit_type);

      state.postType = transformedPostType;
      state.postWidth = PostTypes[transformedPostType].width;
      state.postHeight = PostTypes[transformedPostType].height;
      state.activeTemplate = action.payload.attributes.template_id === 0 ? templates.Standard : templates.Standard2;
      state.postThumbnailFitType = postThumbnailType;
      state.backgroundImage = action.payload.attributes.post_thumbnail;
      state.singleField = {id: '', style: {fontSize: '1'}};
      state.step = postGeneratorSteps.postTemplate;
      state.fields = [
        {
          id: 'title',
          style: {fontSize: action.payload.attributes.title_font_size || '1'},
          value: action.payload.attributes.title || '',
        },
        {
          id: 'subTitle',
          style: {fontSize: action.payload.attributes.description_font_size || '1'},
          value: action.payload.attributes.description || '',
        },
      ];
    },
  },
});

export default postGeneratorSlice;

export const {
  setArchiveValuesAC,
  setIsPriceCheckedAC,
  setStepAC,
  setPostTypeAC,
  setSingleFieldAC,
  setFieldsAC,
  setFontAC,
  setBackgroundImageAC,
  setActiveTemplateAC,
  setInitialState,
  setPostThumbnailFitTypeAC,
} = postGeneratorSlice.actions;
